import { useEffect, useState } from "react";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Box, Paper, Typography, Skeleton, Alert, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { formatCurrency } from "../utils/formatters";
import apiService from "../../../service/Api";

interface CommissionData {
  total_referrals: number;
  conversion_rate: number;
  lifetime_earned: number;
  pending: number;
}
interface CommissionResponse {
  refferal_program_id: number;
  refferal_name: string;
  commission: {
    total_referrals: number;
    conversion_rate: number;
    lifetime_earned: number;
    pending: number;
  };
}

interface Props {
  referralProgramId: string | number;
}

const statCardSx = {
  backgroundColor: "#F3F5F8",
  borderRadius: "16px",
  p: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.5,
};

export default function CommissionCard({ referralProgramId }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState<CommissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!referralProgramId) return;

    const fetchCommission = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await apiService.get<CommissionResponse>(
          `/api/refferal-program/refferal-programs/${referralProgramId}/commission-summary/`,
        );

        setData(response.data.commission);
      } catch {
        setError("Failed to load commission data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCommission();
  }, [referralProgramId]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "20px",
        p: 2.5,
        backgroundColor: colors.primary[900],
      }}
    >
      <Typography sx={{ fontSize: "1rem", fontWeight: 600, mb: 2.5 }}>
        Commission
      </Typography>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
        }}
      >
        {/* Total Referrals */}
        <Box sx={statCardSx}>
          <GroupsOutlinedIcon sx={{ fontSize: 20, color: "#6E7885" }} />
          {loading ? (
            <Skeleton width={40} />
          ) : (
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 700 }}>
              {data?.total_referrals ?? "N/A"}
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.78rem", color: "#7C8798" }}>
            Total Referrals
          </Typography>
        </Box>

        {/* Conversion */}
        <Box sx={statCardSx}>
          <TrendingUpOutlinedIcon sx={{ fontSize: 20, color: "#6E7885" }} />
          {loading ? (
            <Skeleton width={40} />
          ) : (
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 700 }}>
              {data?.conversion_rate != null
                ? `${data.conversion_rate}%`
                : "N/A"}
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.78rem", color: "#7C8798" }}>
            Conversion
          </Typography>
        </Box>

        {/* Lifetime Earned */}
        <Box sx={statCardSx}>
          <AttachMoneyOutlinedIcon sx={{ fontSize: 20, color: "#6E7885" }} />
          {loading ? (
            <Skeleton width={60} />
          ) : (
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 700 }}>
              {data ? formatCurrency(data.lifetime_earned) : "N/A"}
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.78rem", color: "#7C8798" }}>
            Lifetime Earned
          </Typography>
        </Box>

        {/* Pending */}
        <Box sx={statCardSx}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: "#6E7885" }} />
          {loading ? (
            <Skeleton width={60} />
          ) : (
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 700 }}>
              {data ? formatCurrency(data.pending) : "N/A"}
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.78rem", color: "#7C8798" }}>
            Pending
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
